import { useEffect, useState, useRef } from 'react';

import encompassFields from '../../data/encompass_fields.json';
import encompassFieldsDSCR from '../../data/encompass_fields_DSCR.json';
import { getAccessToken } from "../../services/authApi";
import { fieldReader, fieldWriter, updateLienHolderFields } from '../../services/loanApi';
import { LOAN_EDIT_LOCK_FIELD_IDS, isLoanEditLocked } from '../../utils/loanEditLock';
import { submitREOData, submitVODData, fetchVODs } from '../../services/loanSubCollectionApi';
import { submitResidences } from '../../services/residenceApi';
import { submitEmployment } from '../../services/employmentApi';
import { getBPCPrograms, getPageFieldVisibility } from '../../services/userApi';
import { evaluateAndRemoveIncomes, matchesNonIncomeScenario } from '../../services/borrowerApi';
import { error as logAppInsightError } from '../../utils/logger';
import { logError } from "../../utils/logError";
import { toEncompassInteger } from '../../utils/formUtils';
import { generateFilledPDF } from '../../services/documentApi';
import { getExistingDocumentId, createDocument, getAttachmentUploadUrl, uploadAttachmentBlob, assignAttachmentToDocument } from '../../services/documentApi';
import Modal from '../shared/Modal';
import Toast from '../shared/Toast';
import LoadingOverlay from '../shared/LoadingOverlay';

import FinancingRequest from './FinancingRequest';
import PropertyInformation from './PropertyInformation';
import AssetInformation from './AssetInformation';
import REOInformation from './REOInformation';
import ApplicantInformation from './ApplicantInformation';
import PropertyAndMoneyDetails from './PropertyAndMoneyDetails';
import AboutYourFinances from './AboutYourFinances';
import ApplicantDemographics from './ApplicantDemographics';
import LoanOriginatorInformation from './LoanOriginatorInformation';
import AdditionalInformation from './AdditionalInformation';
import BorrowerPairSection from './BorrowerPairSection';
import DirtyCheckPrompt from "../shared/DirtyCheckPrompt";
import RestrictedAccess from './RestrictedAccess';
import LoanActionButtons from './LoanActionButtons';
import { LOAN_PROGRAM_CODES, getAdditionalInfoFields } from '../../config/organizations';
import { isLoanProgram, toCanonicalLoanProgram } from '../../utils/loanProgramUtil';

const DIRTY_FLAG_KEY = "loan_additional_info_dirty";

export default function Index() {
    const [token, setToken] = useState(null);
    const [fieldValues, setFieldValues] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadingText, setLoadingText] = useState('Loading...');
    const [loanid, setLoanid] = useState('');
    const [showToast, setShowToast] = useState(false);
    const [showToastText, setShowToastText] = useState('Loan saved successfully...');
    const [showErrorToast, setShowErrorToast] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [skipFetch, setSkipFetch] = useState(false);
    const [errorTitle, setErrorTitle] = useState("Submission Error");
    const [documentationType, setDocumentationType] = useState("Income");
    const [fieldsJson, setFieldsJson] = useState(encompassFields);
    const [reoList, setReoList] = useState([]);
    const [assignedVolsMap, setAssignedVolsMap] = useState({});
    const [applicationId, setApplicationId] = useState("");
    const [vodList, setVodList] = useState([]);
    const [toastVisible, setToastVisible] = useState(false);
    const [toastAnimation, setToastAnimation] = useState("animate-fade-in-right");
    const reoRef = useRef();
    const [warningToastVisible, setWarningToastVisible] = useState(false);
    const [warningToastMessage, setWarningToastMessage] = useState("");
    const [initialSubmitDate, setInitialSubmitDate] = useState('');
    // Read-only before submittal too, once disclosures are out (see utils/loanEditLock).
    const [editLocked, setEditLocked] = useState(false);
    const [borrowerIndex, setBorrowerIndex] = useState(1);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedBorrowerLegacyId, setSelectedBorrowerLegacyId] = useState('');
    const borrowerPairRef = useRef(null);
    const [showDirtyPrompt, setShowDirtyPrompt] = useState(false);
    const [bpcPrograms, setBpcPrograms] = useState([]);
    const loadedDateMinsRef = useRef({});
    const [isEncompassWeb, setIsEncompassWeb] = useState(false);
    const [isConsumerLoan, setIsConsumerLoan] = useState(false);
    const [hiddenAdditionalFieldIds, setHiddenAdditionalFieldIds] = useState([]);
    const [correspondentChannel, setCorrespondentChannel] = useState(false);
    const [additionalFields, setAdditionalFields] = useState([]);
    const [docTypeForAdditionalFields, setDocTypeForAdditionalFields] = useState("");
    useEffect(() => {
        if (window.elli && window.elli.script && window.elli.script.guest) {
            window.elli.script.guest.create();

            try {
                window.elli.script.getObject("loan").then(async function (loanObject) {
                    const raw = await loanObject.all();
                    const loan = raw?.loan ?? raw;
                    // Fetch 2626 field to determine which JSON to load
                    try {
                        const tpoAuthObject = await window.elli.script.getObject("auth");
                        const authCode = await tpoAuthObject.createAuthCode();
                        const token = await getAccessToken(authCode);
                        // Use fieldReader to get 2626 value
                        const data = await fieldReader(token, ["2626", "CX.DOC.TYPE"], loan.id);
                        const docType = (data["CX.DOC.TYPE"] || "").toString().trim();
                        setDocTypeForAdditionalFields(docType);
                        const val2626 = (data["2626"] || "").toString().trim();
                        let json;
                        if (val2626?.toLowerCase() === "correspondent") {
                            console.log("Loading correspondent business additional fields JSON");
                            setCorrespondentChannel(true);
                        }
                        else {
                            console.log("Loading  business additional fields JSON");
                            json = await import('../../data/business/additionalFields.json');
                        }
                        setAdditionalFields(getAdditionalInfoFields(json.default || json));
                    } catch (e) {
                        // fallback to consumer if error
                        const json = await import('../../data/business/additionalFields.json');
                        setAdditionalFields(getAdditionalInfoFields(json.default || json));
                    }

                    setIsEncompassWeb(!!raw?.loan);

                    setLoanid(loan.id);
                    setApplicationId(loan.applications?.[0]?.id || "");

                    const loanTypeField = loan.customFields?.find(
                        f => f.fieldName === "CX.LOAN.TYPE"
                    );

                    const loanType = (loanTypeField?.value || "").toString().trim().toUpperCase();

                    if (loanType === "CONSUMER") {
                        setIsConsumerLoan(true);
                        setLoading(false);
                        return; // stop further processing
                    }

                    // Read Broker Product + Doc Type from loan custom fields
                    const brokerProductField = loan.customFields?.find(
                        f => f.fieldName === "CX.LOAN.PROD.BROKER.SELECT"
                    );
                    const docTypeField = loan.customFields?.find(
                        f => f.fieldName === "CX.DOC.TYPE"
                    );

                    // This value comes straight off the loan object rather than
                    // through fieldReader, so it is still this Encompass
                    // instance's product name - canonicalize it by hand.
                    const brokerProduct = toCanonicalLoanProgram(brokerProductField?.value);
                    const encompassDocType = (docTypeField?.value || "").toString().trim();

                    // Income mode rules:
                    // 1) ACCELERATOR INCOME / BREEZY INCOME => Income
                    // 2) ACCELERATOR / BREEZY + (Full Doc or Alt Doc) => Income
                    const dtUpper = encompassDocType.toUpperCase();

                    const isIncomeMode =
                        isLoanProgram(brokerProduct, LOAN_PROGRAM_CODES.PROGRAM_003) ||
                        (isLoanProgram(brokerProduct, LOAN_PROGRAM_CODES.PROGRAM_002) &&
                            (dtUpper === "FULL DOC" || dtUpper === "ALT DOC"));

                    const docType = isIncomeMode ? "Income" : "DSCR";

                    const tpo = loan?.tpo || {};
                    const submittedDate = tpo.initialSubmitDate || "";
                    setInitialSubmitDate(submittedDate);

                    setDocumentationType(docType);
                    setFieldsJson(docType === "Income" ? encompassFields : encompassFieldsDSCR);

                    await getToken(false);
                });
            } catch (error) {
                const errorCode = logError("COMMERCIAL_APP_LOAN_FETCH_ERROR");
                logAppInsightError("Loan Fetch Error", {
                    component: "Index",
                    problemId: errorCode,
                    errorCode,
                    rawError: error,
                    errorMessage: `${errorCode} - Failed to fetch loan data from guest script.`
                });
                console.error("Auth block Error:", error);
            }
        } else {
            console.warn("elli.script.guest is not available.");
        }
    }, []);

    const isYes = (val) => {
        const normalized = (val ?? "").toString().trim().toLowerCase();
        return val === true || normalized === "y" || normalized === "yes" || normalized === "true";
    };

    const getToken = async (skipFieldLoad = false) => {
        setLoading(true);

        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const token = await getAccessToken(authCode);
            setToken(token);
            setSkipFetch(skipFieldLoad);
        } catch (err) {
            const errorCode = logError("COMMERCIAL_APP_TOKEN_FETCH_ERROR");
            logAppInsightError("Token Fetch Error", {
                component: "Index",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed to fetch access token for loan.`
            });
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!token || !loanid || !borrowerIndex) return;
        setLoading(true);
        fetchFields();
    }, [borrowerIndex]);

    const getFieldIdWithIndex = (field, borrowerIndex) => {
        if (field.citizenshipField) {
            // Custom logic for citizenship fields
            if (field.id === "CX.CITIZENSHIP.TYPE") {
                return borrowerIndex === 1 ? field.id : `${field.id}.P${borrowerIndex}`;
            }
            if (field.id === "CX.CITIZENSHIP.TYPE.COB") {
                return borrowerIndex === 1 ? field.id : `${field.id}.P${borrowerIndex}`;
            }
        }

        // Default logic for other borrower fields
        return field.isBorrowerField ? `${field.id}#${borrowerIndex}` : field.id;
    };

    const fetchFields = async () => {
        const fieldIds = [...new Set([
            ...fieldsJson.map(f => getFieldIdWithIndex(f, borrowerIndex)),
            ...additionalFields.map(field => field.id),
            "TPO.X16", // Org ID
            ...LOAN_EDIT_LOCK_FIELD_IDS
        ])];

        setLoading(true);
        let locked = false;
        try {
            const data = await fieldReader(token, fieldIds, loanid);
            locked = isLoanEditLocked(data);
            setEditLocked(locked);

            const cleanData = {};
            for (const key in data) {
                let baseKey = key;

                // strip borrower index suffix: "4000#1" -> "4000"
                if (baseKey.includes("#")) {
                    baseKey = baseKey.split("#")[0];
                }

                // strip pair suffix ONLY for P2..P6: "CX.CITIZENSHIP.TYPE.P2" -> "CX.CITIZENSHIP.TYPE"
                // keep P1 as-is (your US credit fields are P1)
                baseKey = baseKey.replace(/\.P[2-6]$/, "");

                cleanData[baseKey] = data[key];
            }

            setFieldValues(cleanData);

            // Track "Start Date" baseline for date fields that require minFromLoaded validation
            try {
                (additionalFields || []).forEach(f => {
                    if (f.datatype !== "date") return;
                    if (!f.validation || !f.validation.minFromLoaded) return;

                    const raw = cleanData[f.id];
                    const v = (raw || "").toString().trim();

                    // ignore blank and "//"
                    if (!v || v === "//") return;

                    // Always update baseline on refresh (after save, this becomes the new Start Date)
                    loadedDateMinsRef.current[f.id] = v;
                });
            } catch (e) {
                console.warn("Failed to set loadedDateMinsRef", e);
            }

            // Use ONLY OrgId from TPO.X16
            try {
                const orgId = (cleanData["TPO.X16"] || "").trim();

                if (orgId) {
                    const [programs, pageVisibility] = await Promise.all([
                        getBPCPrograms(orgId),
                        getPageFieldVisibility(orgId, "CommericalFormPage")
                    ]);

                    setBpcPrograms(programs);
                    setHiddenAdditionalFieldIds(pageVisibility?.hiddenFields || []);
                } else {
                    setBpcPrograms([]);
                    setHiddenAdditionalFieldIds([]);
                }
            } catch (err) {
                console.error("Failed to fetch BPC Programs / Page Field Visibility using OrgId (TPO.X16)", err);
                setHiddenAdditionalFieldIds([]);
            }

            // Load initial VODs
            await refreshVodList();

        } catch (err) {
            const errorCode = logError("COMMERCIAL_APP_FIELD_FETCH_ERROR");
            logAppInsightError("Field Fetch Error", {
                component: "Index",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed to fetch field values from Encompass.`
            });
            console.error("Field fetch failed", err);
        } finally {
            if ((initialSubmitDate && initialSubmitDate !== "//") || locked || isEncompassWeb) {
                setTimeout(() => {
                    makeFormReadOnly();
                }, 100);
            }
            setLoading(false);
        }
    };

    const makeFormReadOnly = () => {
        const readonlyExcludeFields = [
            'CX.Broker.YSP.PCT'
        ];

        const selectors = [
            'input:not([type=hidden]):not([readonly]):not([data-force-edit])',
            'textarea:not([readonly]):not([data-force-edit])',
            'select:not([disabled]):not([data-force-edit])',
            'input[type=checkbox]:not([disabled]):not([data-force-edit])',
            'button:not([data-allow])'
        ];

        const elements = document.querySelectorAll(selectors.join(', '));

        elements.forEach(el => {
            // Skip borrower pair select and its icons
            if (
                el.id === 'borrower-pair' ||
                el.id === "print" ||
                el.closest('#borrower-pair-wrapper') // wrap icons with this ID
            ) {
                return;
            }

            if (readonlyExcludeFields.some(removeFieldId => `field-${removeFieldId.toLowerCase()}` === el.id.toLowerCase())) {
                return;
            }

            switch (el.tagName) {
                case 'BUTTON':
                    break;
                case 'INPUT':
                    el.setAttribute('readOnly', true);
                    break;
                case 'TEXTAREA':
                    el.setAttribute('readOnly', true);
                    break;
                default:
                    el.setAttribute('disabled', true);
            }

            el.classList.add('cursor-not-allowed');
        });
    };

    useEffect(() => {
        if (!token) return;

        if (skipFetch) {
            setSkipFetch(false);
            return;
        }

        fetchFields();

    }, [token]);

    const refreshVodList = async () => {
        try {
            const response = await fetchVODs(token, loanid, applicationId);

            const mapped = response.map(vod => ({
                holderName: vod.holderName || '',
                items: vod.items || [{ itemNumber: 1, accountIdentifier: '', urla2020CashOrMarketValueAmount: '' }],
                isExisting: true,
                isNew: false
            }));

            // Pad to 8 rows
            while (mapped.length < 8) {
                mapped.push({
                    holderName: '',
                    items: [{ itemNumber: 1, accountIdentifier: '', urla2020CashOrMarketValueAmount: '' }],
                    isExisting: false,
                    isNew: false
                });
            }

            setVodList(mapped.slice(0, 8));
        } catch (error) {
            console.error("Failed to refresh VOD list:", error);
        }
    };

    const hasResidenceData = (residence) => {
        return [
            residence.addressStreetLine1,
            residence.addressCity,
            residence.addressState,
            residence.addressPostalCode
        ].some(v => (v || "").toString().trim() !== "");
    };

    const buildBorrowerResidences = () => {
        const currentForeign = isYes(getUiFieldValue("FR0129", getFieldDef("FR0129")));
        const priorForeign = isYes(getUiFieldValue("FR0329", getFieldDef("FR0329")));

        const currentZip = document.getElementById("field-FR0108")?.value?.trim() || "";
        const currentCountryRaw = currentForeign ? getVal("FR0130") : "US";
        const clearCurrentCountry = (!currentZip && currentCountryRaw === "US");

        const priorZip = document.getElementById("field-FR0308")?.value?.trim() || "";
        const priorCountryRaw = priorForeign ? getVal("FR0330") : "US";
        const clearPriorCountry = (!priorZip && priorCountryRaw === "US");

        const currentResidence = {
            id: '',
            residencyType: "Current",
            addressCity: document.getElementById("field-FR0106")?.value || "",
            addressPostalCode: currentZip,
            addressState: getUiFieldValue("FR0107", getFieldDef("FR0107")) || "",
            addressStreetLine1: document.getElementById("field-FR0126")?.value || "",
            durationTermMonths: 0,
            durationTermYears: parseInt(document.getElementById("field-FR0112")?.value || "0", 10),
            residencyBasisType: document.querySelector('input[name="field-FR0115"]:checked')?.value || "",
            urla2020StreetAddress: document.getElementById("field-FR0126")?.value || "",
            country: clearCurrentCountry ? "" : currentCountryRaw,
            countryCode: clearCurrentCountry ? "" : (currentForeign ? null : "US"),
            foreignAddressIndicator: currentForeign
        };

        const priorResidence = {
            id: '',
            residencyType: "Prior",
            addressCity: document.getElementById("field-FR0306")?.value || "",
            addressPostalCode: priorZip,
            addressState: getUiFieldValue("FR0307", getFieldDef("FR0307")) || "",
            addressStreetLine1: document.getElementById("field-FR0326")?.value || "",
            durationTermMonths: 0,
            durationTermYears: parseInt(document.getElementById("field-FR0312")?.value || "0", 10),
            residencyBasisType: document.querySelector('input[name="field-FR0315"]:checked')?.value || "",
            urla2020StreetAddress: document.getElementById("field-FR0326")?.value || "",
            country: clearPriorCountry ? "" : priorCountryRaw,
            countryCode: clearPriorCountry ? "" : (priorForeign ? null : "US"),
            foreignAddressIndicator: priorForeign
        };

        return hasResidenceData(priorResidence)
            ? [currentResidence, priorResidence]
            : [currentResidence];
    };

    const buildCoborrowerResidences = () => {
        const currentForeign = isYes(getUiFieldValue("FR0229", getFieldDef("FR0229")));
        const priorForeign = isYes(getUiFieldValue("FR0429", getFieldDef("FR0429")));

        const currentZip = document.getElementById("field-FR0208")?.value?.trim() || "";
        const currentCountryRaw = currentForeign ? getVal("FR0230") : "US";
        const clearCurrentCountry = (!currentZip && currentCountryRaw === "US");

        const priorZip = document.getElementById("field-FR0408")?.value?.trim() || "";
        const priorCountryRaw = priorForeign ? getVal("FR0430") : "US";
        const clearPriorCountry = (!priorZip && priorCountryRaw === "US");

        const currentResidence = {
            id: '',
            residencyType: "Current",
            addressCity: document.getElementById("field-FR0206")?.value || "",
            addressPostalCode: currentZip,
            addressState: getUiFieldValue("FR0207", getFieldDef("FR0207")) || "",
            addressStreetLine1: document.getElementById("field-FR0226")?.value || "",
            durationTermMonths: 0,
            durationTermYears: parseInt(document.getElementById("field-FR0212")?.value || "0", 10),
            residencyBasisType: document.querySelector('input[name="field-FR0215"]:checked')?.value || "",
            urla2020StreetAddress: document.getElementById("field-FR0226")?.value || "",
            country: clearCurrentCountry ? "" : currentCountryRaw,
            countryCode: clearCurrentCountry ? "" : (currentForeign ? null : "US"),
            foreignAddressIndicator: currentForeign
        };

        const priorResidence = {
            id: '',
            residencyType: "Prior",
            addressCity: document.getElementById("field-FR0406")?.value || "",
            addressPostalCode: priorZip,
            addressState: getUiFieldValue("FR0407", getFieldDef("FR0407")) || "",
            addressStreetLine1: document.getElementById("field-FR0426")?.value || "",
            durationTermMonths: 0,
            durationTermYears: parseInt(document.getElementById("field-FR0412")?.value || "0", 10),
            residencyBasisType: document.querySelector('input[name="field-FR0415"]:checked')?.value || "",
            urla2020StreetAddress: document.getElementById("field-FR0426")?.value || "",
            country: clearPriorCountry ? "" : priorCountryRaw,
            countryCode: clearPriorCountry ? "" : (priorForeign ? null : "US"),
            foreignAddressIndicator: priorForeign
        };

        return hasResidenceData(priorResidence)
            ? [currentResidence, priorResidence]
            : [currentResidence];
    };

    const parseMoney = (v) => {
        const s = (v ?? "").toString().replace(/,/g, "").trim();
        if (!s) return 0;
        const n = parseFloat(s);
        return isNaN(n) ? 0 : n;
    };

    const getVal = (id) => document.getElementById(`field-${id}`)?.value || "";


    const parseIntSafe = (v) => {
        const s = (v ?? "").toString().trim();
        if (!s) return 0;
        const n = parseInt(s, 10);
        return isNaN(n) ? 0 : n;
    };

    const buildBorrowerEmployment = () => {
        const basePayAmount = parseMoney(getVal("FE0119"));
        const overtimeAmount = parseMoney(getVal("FE0120"));
        const bonusAmount = parseMoney(getVal("FE0121"));
        const commissionsAmount = parseMoney(getVal("FE0122"));
        const otherAmount = parseMoney(getVal("FE0123"));
        const years = parseIntSafe(getVal("FE0116"));
        const months = parseIntSafe(getVal("FE0152"));

        const monthlyIncomeAmount =
            basePayAmount +
            overtimeAmount +
            bonusAmount +
            commissionsAmount +
            otherAmount;

        return [{
            basePayAmount,
            overtimeAmount,
            bonusAmount,
            commissionsAmount,
            otherAmount,
            monthlyIncomeAmount,

            employerName: getVal("FE0102"),
            addressStreetLine1: getVal("FE0160"),
            addressCity: getVal("FE0105"),
            addressState: getVal("FE0106"),
            addressPostalCode: getVal("FE0107"),
            phoneNumber: getVal("FE0117"),
            positionDescription: getVal("FE0110"),

            timeInLineOfWorkYears: years,
            jobTermMonths: months,

            currentEmploymentIndicator: true,
            countryCode: "US"
        }];
    };


    const buildCoborrowerEmployment = () => {
        const basePayAmount = parseMoney(getVal("FE0219"));
        const overtimeAmount = parseMoney(getVal("FE0220"));
        const bonusAmount = parseMoney(getVal("FE0221"));
        const commissionsAmount = parseMoney(getVal("FE0222"));
        const otherAmount = parseMoney(getVal("FE0223"));

        const years = parseIntSafe(getVal("FE0216"));
        const months = parseIntSafe(getVal("FE0252"));

        const monthlyIncomeAmount =
            basePayAmount +
            overtimeAmount +
            bonusAmount +
            commissionsAmount +
            otherAmount;

        return [{
            basePayAmount,
            overtimeAmount,
            bonusAmount,
            commissionsAmount,
            otherAmount,
            monthlyIncomeAmount,

            employerName: getVal("FE0202"),
            addressStreetLine1: getVal("FE0260"),
            addressCity: getVal("FE0205"),
            addressState: getVal("FE0206"),
            addressPostalCode: getVal("FE0207"),
            phoneNumber: getVal("FE0217"),
            positionDescription: getVal("FE0210"),

            timeInLineOfWorkYears: years,
            jobTermMonths: months,

            currentEmploymentIndicator: true,
            countryCode: "US"
        }];
    };

    // reads the current UI value for a given field id using your existing conventions
    const getUiFieldValue = (rawId, fieldMeta) => {
        const escapedId = rawId.replace(/\./g, '\\.');
        const yesCheckbox = document.getElementById(`field-${rawId}-yes`);
        const noCheckbox = document.getElementById(`field-${rawId}-no`);

        // Pair of Yes/No checkboxes
        if (yesCheckbox && noCheckbox) {
            if (fieldMeta?.datatype === "YN") {
                return yesCheckbox.checked ? "true" : noCheckbox.checked ? "false" : "";
            }
            return yesCheckbox.checked ? "Y" : noCheckbox.checked ? "N" : "";
        }

        // Multiple checkboxes using same id (checkbox group)
        const groupChecks = document.querySelectorAll(`input[type="checkbox"]#field-${escapedId}`);
        if (groupChecks && groupChecks.length > 1) {
            const checked = [...groupChecks].find(cb => cb.checked);
            return checked?.value || "";
        }

        // Single input (text/select/single checkbox)
        const input = document.getElementById(`field-${rawId}`);
        if (!input) return "";

        if (input.type === 'checkbox') {
            // Keep compatibility with your writer logic
            if (fieldMeta?.datatype === "YN") return input.checked ? "true" : "false";
            if (fieldMeta?.datatype === "Y") return input.checked ? "true" : "";
            return input.checked ? (input.value || "true") : "";
        }

        if (fieldMeta?.datatype === "string" && input.tagName === "SELECT") {
            // For Save, use the selected OPTION's value (you already do this in Save path)
            return input.options[input.selectedIndex]?.value || "";
        }

        let val = input.value || "";
        if (fieldMeta?.datatype === "money" && typeof val === "string") {
            val = val.replace(/,/g, "");
        }
        return val;
    };

    // Get field def from additionalFields first, then fieldsJson (unchanged)
    const getFieldDef = (id) => {
        return additionalFields.find(f => f.id === id) || fieldsJson.find(f => f.id === id);
    };

    // Normalize value for comparisons (unchanged)
    const normalizeValue = (fieldDef, raw) => {
        const v = (raw ?? "").toString().trim();

        if (fieldDef?.format === "YN" || fieldDef?.format === "YN_BOOL") {
            const up = v.toUpperCase();
            if (up === "Y" || up === "YES" || up === "TRUE") return "Yes";
            if (up === "N" || up === "NO" || up === "FALSE") return "No";
        }
        return v.toUpperCase();
    };

    // Helper: check emptiness with allowZero support
    const isEmptyForRule = (raw, { allowZero } = {}) => {
        if (raw === null || raw === undefined) return true;
        const v = raw.toString().trim();
        if (v === "") return true;
        if (allowZero && (v === "0" || v === "0.0" || v === "0.00")) return false;
        return false; // non-empty otherwise
    };

    const validateAdditionalJsonRules = () => {
        const errors = [];
        if (correspondentChannel) return errors; // skip all additional field rules if Correspondent Channel (per request)
        (additionalFields || [])
            .filter(field => !(hiddenAdditionalFieldIds || []).includes(field.id))
            .forEach(field => {
                if (!Array.isArray(field.rules) || field.rules.length === 0) return;

                const thisFieldValRaw = getUiFieldValue(field.id, field);
                const thisFieldDef = getFieldDef(field.id);
                const thisFieldVal = normalizeValue(thisFieldDef, thisFieldValRaw);

                field.rules.forEach(rule => {
                    const trigger = rule?.if?.equals;
                    if (trigger == null) return;
                    const triggerNorm = normalizeValue(thisFieldDef, trigger);

                    if (thisFieldVal === triggerNorm) {
                        const targetId = rule?.then?.field;
                        if (!targetId) return;

                        const targetDef = getFieldDef(targetId) || {};
                        const targetValRaw = getUiFieldValue(targetId, targetDef);

                        // 2a) Conditional "required" rule (with optional allowZero)
                        if (rule?.then?.required === true) {
                            const allowZero = !!rule?.then?.allowZero;
                            if (isEmptyForRule(targetValRaw, { allowZero })) {
                                errors.push({
                                    id: targetId,
                                    message: rule.message || `Field ${targetId} is required.`
                                });
                                return; // skip further checks for this rule after required fail
                            }
                        }

                        // 2b) Conditional equality rule
                        if (rule?.then?.equals != null) {
                            const targetVal = normalizeValue(targetDef, targetValRaw);
                            const mustEqualNorm = normalizeValue(targetDef, rule.then.equals);
                            if (targetVal !== mustEqualNorm) {
                                errors.push({
                                    id: targetId,
                                    message: rule.message || `Field ${targetId} must equal ${rule.then.equals}.`
                                });
                            }
                        }
                    }
                });
            });

        return errors;
    };

    const runJsonDateValidation = () => {
        const errors = [];

        additionalFields
            .filter(f => !(hiddenAdditionalFieldIds || []).includes(f.id))
            .forEach(f => {
                const v = getUiFieldValue(f.id, f);
                const valRules = f.validation || {};

                if (f.datatype === 'date' && v) {
                    // normalize both to yyyy-mm-dd strings
                    const parsedStr = new Date(v).toISOString().slice(0, 10);
                    const todayStr = new Date().toISOString().slice(0, 10);

                    if (valRules.noFuture && parsedStr > todayStr) {
                        errors.push(`${f.name} cannot be a future date.`);
                    }
                    if (valRules.noPast && parsedStr < todayStr) {
                        errors.push(`${f.name} cannot be a past date.`);
                    }

                    // minFromLoaded => BP App Date must be >= "Start Date" (loaded baseline)
                    if (valRules.minFromLoaded) {
                        const baselineRaw = loadedDateMinsRef.current[f.id];
                        if (baselineRaw) {
                            const baselineStr = new Date(baselineRaw).toISOString().slice(0, 10);
                            if (parsedStr < baselineStr) {
                                errors.push(`${f.name} cannot be before the Start Date (${baselineRaw}).`);
                            }
                        }
                    }
                }
            });

        return errors;
    };

    const setOrUpsertField = (arr, id, value) => {
        const idx = arr.findIndex(x => (x.id || "").split("#")[0] === id);
        if (idx >= 0) arr[idx].value = value;
        else arr.push({ id, value });
    };

    const isForeignNational = (val) => {
        return ((val || "").toString().trim().toUpperCase() === "FOREIGN NATIONAL");
    };

    useEffect(() => {
        const handleSingleSelectCheckbox = (e) => {
            const target = e.target;
            if (target?.type !== "checkbox") return;

            const name = target.name;
            if (!name) return;

            // 1️ Enforce single-select behavior
            document
                .querySelectorAll(`input[type="checkbox"][name="${name}"]`)
                .forEach(cb => {
                    if (cb !== target) cb.checked = false;
                });

            // 2️ Clear validation outline for the whole group
            document
                .querySelectorAll(`input[type="checkbox"][name="${name}"]`)
                .forEach(cb => {
                    cb.removeAttribute("data-required-missing-input");
                    cb.style.removeProperty("outline");
                    cb.style.removeProperty("outline-offset");
                });
        };

        document.addEventListener("change", handleSingleSelectCheckbox);
        return () => document.removeEventListener("change", handleSingleSelectCheckbox);
    }, []);

    const scrollToField = (fieldId) => {
        if (!fieldId) return;

        const escaped = fieldId.replace(/\./g, "\\.");

        // Try: normal input/select/textarea
        let el =
            document.getElementById(`field-${fieldId}`) ||
            document.querySelector(`#field-${escaped}`);

        // If not found, try: checkbox group (SingleSelectCheckboxGroup uses repeated same id)
        if (!el) {
            el = document.querySelector(`input[type="checkbox"]#field-${escaped}`);
        }

        // If still not found, try: by name (for your Yes/No group)
        if (!el) {
            el = document.querySelector(`input[type="checkbox"][name="${fieldId}"]`);
        }

        if (!el) return;

        // scroll into view
        try {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch (_) {
            el.scrollIntoView();
        }

        // focus (helps keyboard / accessibility)
        try {
            el.focus({ preventScroll: true });
        } catch (_) {
            try { el.focus(); } catch (__) { }
        }
    };

    const isRequiredNumberEmpty = (value, allowZero = false) => {
        if (value === null || value === undefined) return true;

        const str = String(value).trim();
        if (str === "") return true;

        const num = Number(str);

        // invalid number
        if (Number.isNaN(num)) return true;

        // allow 0 only when explicitly permitted
        return allowZero ? num < 0 : num <= 0;
    };

    const isFieldEmpty = (field, value) => {
        if (field.id === "389") {
            return isRequiredNumberEmpty(value, true); // allow 0
        }

        return value === null || value === undefined || String(value).trim() === "";
    };

    const handleSubmit = async (isRetry = false) => {
        let borrowerRes = buildBorrowerResidences();
        let coborrowerRes = buildCoborrowerResidences();

        let borrowerEmployment = buildBorrowerEmployment();
        let coborrowerEmployment = buildCoborrowerEmployment();

        document.querySelectorAll('[data-required-missing-input]').forEach(el => {
            el.removeAttribute('data-required-missing-input');
            el.style.border = '';
        });

        // After submission all fields are disabled except CX.BROKER.YSP.PCT, so skip required
        // validation for newly introduced fields that older submitted loans won't have values for
        const skipRequiredAfterSubmit = isLoanSubmitted ? ["CX.LIVING.RENT.FREE"] : [];

        // In correspondent scenario, skip required validation for AdditionalInformation fields
        const requiredFields = correspondentChannel
            ? []
            : additionalFields.filter(
                f => f.required
                    && !(hiddenAdditionalFieldIds || []).includes(f.id)
                    && !skipRequiredAfterSubmit.includes(f.id)
            );

        const missingFields = requiredFields.filter(field => {
            const input = document.getElementById(`field-${field.id}`);
            const value = input?.value;
            return isFieldEmpty(field, value);
        });

        const requiredFromEncompass = (fieldsJson || []).filter(f => f.required === true);
        const missingFromEncompass = requiredFromEncompass.filter(f => {
            const uiVal = getUiFieldValue(f.id, f);
            return isFieldEmpty(f, uiVal);
        });

        const crossErrors = validateAdditionalJsonRules();
        let firstToFocus = null;
        if (missingFields.length > 0 || missingFromEncompass.length > 0 || crossErrors.length > 0) {
            const messages = [
                ...missingFields.map(f => `• ${f.name}`),
                ...missingFromEncompass.map(f => `• ${f.name || `Field ${f.id}`}`),
                ...crossErrors.map(e => `• ${e.message}${e.id ? ` (Field ${e.id})` : ''}`)
            ];

            // Highlight only the input/select/textarea — NOT the label or wrapper
            missingFields.forEach(f => {
                const el = document.getElementById(`field-${f.id}`);
                if (!el) return;

                el.setAttribute('data-required-missing-input', 'true');
                el.style.setProperty('border', '1px solid red', 'important');

                if (!firstToFocus && (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA')) {
                    firstToFocus = el;
                }
            });

            crossErrors.forEach(f => {
                const el = document.getElementById(`field-${f.id}`);
                if (!el) return;

                el.setAttribute('data-required-missing-input', 'true');
                el.style.setProperty('border', '1px solid red', 'important');

                if (!firstToFocus && (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA')) {
                    firstToFocus = el;
                }
            });

            missingFromEncompass.forEach(f => {
                const el = document.getElementById(`field-${f.id}`);
                if (!el) return;

                // el.setAttribute('data-required-missing-input', 'true');
                // el.style.setProperty('border', '1px solid red', 'important');

                if (!firstToFocus && (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA')) {
                    firstToFocus = el;
                }
            });

            if (firstToFocus) {
                try { firstToFocus.focus(); } catch (e) { /* ignore */ }
            }

            setWarningToastMessage(`Please fill all required fields:<br/> ${messages.join('<br/> ')}`);
            setWarningToastVisible(true);
            setTimeout(() => setWarningToastVisible(false), 5000);
            setLoading(false);
            return;
        }

        // Require What is the Gross Rent? when Documentation Type is DSCR
        try {
            const docTypeVal = (document.getElementById('field-CX.DOC.TYPE')?.value || "").toString().trim().toUpperCase();
            if (docTypeVal === "DSCR") {
                const grossRentVal = getUiFieldValue("CX.GROSS.RENT", getFieldDef("CX.GROSS.RENT"));
                if (!grossRentVal || grossRentVal.toString().trim() === "") {
                    setWarningToastMessage("Gross Rent is required for DSCR Documentation Type.");
                    setWarningToastVisible(true);
                    setTimeout(() => setWarningToastVisible(false), 5000);
                    setLoading(false);
                    return;
                }
            }
            // if(correspondentChannel) {
            //     if(docTypeForAdditionalFields === "DSCR") {
            //         const grossRentVal = getUiFieldValue("CX.GROSS.RENT", getFieldDef("CX.GROSS.RENT"));    
            //         if (!grossRentVal || grossRentVal.toString().trim() === "") {
            //             setWarningToastMessage("Gross Rent is required for DSCR Documentation Type.");
            //             setWarningToastVisible(true);
            //             setTimeout(() => setWarningToastVisible(false), 5000);
            //             setLoading(false);
            //             return;
            //         }
            //     }
            // }
        } catch (vErr) {
            // defensive: if anything goes wrong reading DOM, show a toast and abort save
            setWarningToastMessage("Validation failed while checking Gross Rent. Please ensure the form is filled correctly.");
            setWarningToastVisible(true);
            setTimeout(() => setWarningToastVisible(false), 5000);
            setLoading(false);
            return;
        }


        // Require CEMA Yes/No when state is NY AND it's a refinance
        try {
            const stateVal = (document.getElementById('field-14')?.value || "").toString().trim().toUpperCase();
            if (isRefiOrCashOut && stateVal === "NY") {
                const cemaDef = getFieldDef("CX.CEMA.NY.PROP");
                const cemaVal = getUiFieldValue("CX.CEMA.NY.PROP", cemaDef); // returns "Y" / "N" / "" (or "Yes"/"No" depending on your normalize)
                if (!cemaVal || cemaVal.toString().trim() === "") {
                    setWarningToastMessage("Please select Yes or No for:<BR/> Is this Refinance being completed as a New York CEMA.");
                    setWarningToastVisible(true);
                    setTimeout(() => setWarningToastVisible(false), 5000);
                    setLoading(false);
                    return;
                }
            }
        } catch (vErr) {
            // defensive: if anything goes wrong reading DOM, show a toast and abort save
            setWarningToastMessage("Validation failed while checking CEMA. Please ensure the form is filled correctly.");
            setWarningToastVisible(true);
            setTimeout(() => setWarningToastVisible(false), 5000);
            setLoading(false);
            return;
        }

        // ================= Foreign National US Credit Validation + Save Clearing =================
        try {
            // Your US credit fields are P1 fields and your UI only shows them for borrowerIndex === 1.
            // We'll enforce the rule only when borrowerIndex === 1.
            const borRes = getUiFieldValue("CX.CITIZENSHIP.TYPE", getFieldDef("CX.CITIZENSHIP.TYPE"));
            const cobRes = getUiFieldValue("CX.CITIZENSHIP.TYPE.COB", getFieldDef("CX.CITIZENSHIP.TYPE.COB"));

            const borIsFN = borrowerIndex === 1 && isForeignNational(borRes);
            const cobIsFN = borrowerIndex === 1 && isForeignNational(cobRes);

            // read selected Yes/No for US credit
            const borCredit = getUiFieldValue("CX.US.CREDIT.HIST.BOR.P1", getFieldDef("CX.US.CREDIT.HIST.BOR.P1"));
            const cobCredit = getUiFieldValue("CX.US.CREDIT.HIST.COB.P1", getFieldDef("CX.US.CREDIT.HIST.COB.P1"));

            const missing = [];
            const highlight = [];

            if (borIsFN) {
                const v = (borCredit || "").toString().trim().toUpperCase();
                if (v !== "Y" && v !== "N") {
                    missing.push("• Does Foreign National Borrower have U.S. Credit? (Yes/No required)");
                    highlight.push("CX.US.CREDIT.HIST.BOR.P1");
                }
            }

            if (cobIsFN) {
                const v = (cobCredit || "").toString().trim().toUpperCase();
                if (v !== "Y" && v !== "N") {
                    missing.push("• Does Foreign National Co-Borrower have U.S. Credit? (Yes/No required)");
                    highlight.push("CX.US.CREDIT.HIST.COB.P1");
                }
            }

            if (missing.length > 0) {
                // Highlight the checkbox group inputs (SingleSelectCheckboxGroup uses repeated same id)
                highlight.forEach((id) => {
                    const escaped = id.replace(/\./g, "\\.");
                    const inputs = document.querySelectorAll(`input[type="checkbox"]#field-${escaped}`);
                    inputs.forEach(el => {
                        el.setAttribute("data-required-missing-input", "true");
                        el.style.setProperty("outline", "2px solid red", "important");
                        el.style.setProperty("outline-offset", "2px", "important");
                    });
                });

                setWarningToastMessage(`Please select Yes or No:<br/> ${missing.join("<br/>")}`);
                setWarningToastVisible(true);
                setTimeout(() => setWarningToastVisible(false), 5000);
                setLoading(false);
                if (highlight.length > 0) {
                    scrollToField(highlight[0]);
                }
                return;
            }
        } catch (vErr) {
            setWarningToastMessage("Validation failed while checking Foreign National U.S. Credit fields.");
            setWarningToastVisible(true);
            setTimeout(() => setWarningToastVisible(false), 5000);
            setLoading(false);
            return;
        }


        const dateErrors = runJsonDateValidation();
        if (dateErrors.length > 0) {
            setWarningToastMessage(
                `Please fix the following before saving:<br/> ${dateErrors.map(e => `• ${e}`).join('<br/> ')}`
            );
            setWarningToastVisible(true);
            setTimeout(() => setWarningToastVisible(false), 5000);
            setLoading(false);
            return;
        }

        setLoadingText('Saving...');
        setLoading(true);
        await Promise.resolve();

        // Determine how many asset rows already exist in Encompass
        const existingAssetCount = vodList.filter(v => v.isExisting).length;

        // Get allowed field IDs (rows 1–existingAssetCount only)
        const allowedFieldIds = new Set();
        const assetFieldGroups = [
            ['DD0102', 'DD0110', 'DD0148'],
            ['DD0202', 'DD0210', 'DD0248'],
            ['DD0302', 'DD0310', 'DD0348'],
            ['DD0402', 'DD0410', 'DD0448'],
            ['DD0502', 'DD0510', 'DD0548'],
            ['DD0602', 'DD0610', 'DD0648'],
            ['DD0702', 'DD0710', 'DD0748'],
            ['DD0802', 'DD0810', 'DD0848'],
        ];

        for (let i = 0; i < existingAssetCount; i++) {
            assetFieldGroups[i].forEach(id => allowedFieldIds.add(id));
        }

        const hiddenAdditionalFieldSet = new Set(hiddenAdditionalFieldIds || []);

        // If correspondentChannel, exclude all Additional Information fields from save payload
        let allFields;
        if (correspondentChannel) {
            allFields = fieldsJson.filter(f => f.IsUpdate);
        } else {
            allFields = [
                ...fieldsJson.filter(f => f.IsUpdate),
                ...additionalFields
                    .filter(f => f.isUpdate !== false)
                    .filter(f => !hiddenAdditionalFieldSet.has(f.id))
                    .map(f => ({ ...f, IsUpdate: true }))
            ];
        }

        const updatedFields = allFields
            .map(f => {
                const rawId = f.id;
                const escapedId = rawId.replace(/\./g, '\\.');
                const fieldId = `field-${rawId}`;
                const fieldMeta = allFields.find(field => field.id === rawId);

                const yesCheckbox = document.getElementById(`field-${rawId}-yes`);
                const noCheckbox = document.getElementById(`field-${rawId}-no`);

                let value;
                if (yesCheckbox && noCheckbox) {
                    // Yes/No checkbox pair logic
                    if (fieldMeta?.datatype === "YN") {
                        value = yesCheckbox.checked ? "true" : noCheckbox.checked ? "false" : "";
                    } else {
                        value = yesCheckbox.checked ? "Y" : noCheckbox.checked ? "N" : "";
                    }
                } else {
                    // Single checkbox or other inputs
                    const input = document.getElementById(fieldId);
                    if (input?.type === 'checkbox') {
                        if (fieldMeta?.datatype === "YN") {
                            value = input.checked ? "true" : "false";
                        }
                        else if (fieldMeta?.datatype === "Y") {
                            value = input.checked ? "true" : "";
                        }
                        else {
                            // Check if it's a checkbox group (multiple with same ID)
                            const checkboxes = document.querySelectorAll(`input[type="checkbox"]#field-${escapedId}`);
                            if (checkboxes.length > 1) {
                                const checked = [...checkboxes].find(cb => cb.checked);


                                return {
                                    id: getFieldIdWithIndex(f, borrowerIndex),
                                    value: checked?.value || fieldValues[rawId] || ""
                                };
                            }

                            // Handle regular input (text, single checkbox, etc.)
                            const input = document.getElementById(`field-${rawId}`);
                            if (input) {
                                if (input.type === 'checkbox') {
                                    if (!input.checked && fieldValues[rawId]) {
                                        value = fieldValues[rawId];
                                    } else {
                                        value = input.checked ? input.value || "true" : "";
                                    }
                                } else {
                                    value = input.value;
                                }
                            } else if (fieldValues[rawId]) {
                                value = fieldValues[rawId];
                            } else {
                                value = "";
                            }
                        }
                    } else {
                        if (fieldMeta?.datatype === "string" && input?.tagName === "SELECT") {
                            value = input.options[input.selectedIndex]?.value || "";
                        } else {
                            value = input?.value || "";
                            if (fieldMeta?.datatype === "money" && typeof value === "string") {
                                value = value.replace(/,/g, "");
                            }
                        }
                    }
                }

                // Encompass 356 (Appraised Value) does NOT accept decimals
                if (rawId === "356") {
                    value = toEncompassInteger(value);
                }

                return {
                    id: getFieldIdWithIndex(f, borrowerIndex),
                    value
                };
            })
            .filter(f => {
                // Skip asset fields that are not in the allowedFieldIds set
                const baseId = f.id.split("#")[0]; // for DD0102#1
                if (baseId.startsWith("DD") && !allowedFieldIds.has(baseId)) {
                    return false;
                }
                return true;
            });

        let brokerVal = (fieldValues["CX.LOAN.PROD.BROKER.SELECT"] || "").toString();
        let docTypeVal = (fieldValues["CX.DOC.TYPE"] || "").toString();
        let incomeTypeVal = (fieldValues["CX.INCOME.TYPE"] || "").toString();

        // set 1005 = CX.GROSS.RENT (unless non-income scenario -> clear it)
        const rent = (document.getElementById("field-CX.GROSS.RENT")?.value || "").replace(/,/g, "").trim();

        // Remove rental income if a non-income product is selected
        const matched = matchesNonIncomeScenario(brokerVal, docTypeVal, incomeTypeVal);

        const i = updatedFields.findIndex(x => (x.id || "").split("#")[0] === "1005");
        const val = matched ? "" : rent;

        if (i >= 0) updatedFields[i].value = val;
        else updatedFields.push({ id: "1005", value: val });

        // ================= Force-clear US Credit fields unless Foreign National =================
        try {
            const borRes = getUiFieldValue("CX.CITIZENSHIP.TYPE", getFieldDef("CX.CITIZENSHIP.TYPE"));
            const cobRes = getUiFieldValue("CX.CITIZENSHIP.TYPE.COB", getFieldDef("CX.CITIZENSHIP.TYPE.COB"));

            const borIsFN = borrowerIndex === 1 && isForeignNational(borRes);
            const cobIsFN = borrowerIndex === 1 && isForeignNational(cobRes);

            if (!borIsFN) {
                setOrUpsertField(updatedFields, "CX.US.CREDIT.HIST.BOR.P1", "");
            }

            if (!cobIsFN) {
                setOrUpsertField(updatedFields, "CX.US.CREDIT.HIST.COB.P1", "");
            }

            // If not Pair-1, these questions don't apply; keep them empty to avoid stale values
            if (borrowerIndex !== 1) {
                setOrUpsertField(updatedFields, "CX.US.CREDIT.HIST.BOR.P1", "");
                setOrUpsertField(updatedFields, "CX.US.CREDIT.HIST.COB.P1", "");
            }
        } catch (e) {
            // non-blocking; if something fails here, do not block save
            console.warn("Failed to force-clear US Credit fields", e);
        }


        try {
            if (!isLoanSubmitted) {
                await fieldWriter(token, loanid, updatedFields);
            } else {
                const brokerYSP = updatedFields.filter(item => item.id.toLowerCase() === "CX.Broker.YSP.PCT".toLowerCase());
                await fieldWriter(token, loanid, brokerYSP);
            }

            if (!isRetry) {
                try {
                    if (!isLoanSubmitted) {
                        await submitREOData(reoList, assignedVolsMap, token, loanid, applicationId);
                        await submitResidences({
                            token,
                            loanId: loanid,
                            applicationId,
                            borrowerResidences: borrowerRes,
                            coborrowerResidences: coborrowerRes
                        });

                        if (documentationType === "Income") {
                            await submitEmployment({
                                token,
                                loanId: loanid,
                                applicationId,
                                borrowerEmployment: borrowerEmployment,
                                coborrowerEmployment: coborrowerEmployment
                            });
                        }

                        await submitVODData(vodList, setVodList, token, loanid, applicationId);
                        await updateLienHolderFields(token, loanid);
                        if (borrowerPairRef.current?.reload) {
                            borrowerPairRef.current.reload(selectedBorrowerLegacyId);
                        }

                        const evalResult = await evaluateAndRemoveIncomes(
                            token,
                            loanid,
                            updatedFields,
                            fieldValues
                        );
                    }

                    setLoading(true);
                    fetchFields();

                } catch (submissionError) {
                    const errorCode = logError("COMMERCIAL_APP_REO_VOD_SUBMIT_ERROR");
                    logAppInsightError("REO/VOD Submission Error", {
                        component: "Index",
                        problemId: errorCode,
                        errorCode,
                        rawError: submissionError,
                        errorMessage: `${errorCode} - Failed to submit REO or VOD data to Encompass.`
                    });
                    throw submissionError; // Re-throw so outer catch handles retry or toast
                }

                // Refresh VODs
                await refreshVodList();

                // Now refresh REO data from the child component
                if (reoRef.current?.refreshREO) {
                    await reoRef.current.refreshREO();
                }

                // After all successful save operations complete
                localStorage.removeItem(DIRTY_FLAG_KEY);
                window.dispatchEvent(new StorageEvent("storage", { key: DIRTY_FLAG_KEY, newValue: "false" }));

                setShowToastText('Loan saved successfully...');
                setShowToast(true);

                setTimeout(() => setShowToast(false), 10000);
            }
        } catch (error) {
            const errorCode = logError("COMMERCIAL_APP_FIELD_WRITE_ERROR");
            logAppInsightError("Field Write Error", {
                component: "Index",
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - Failed to write field data to Encompass.`
            });

            if (error.response?.status === 401 && !isRetry) {
                console.warn('401 detected, regenerating token...');
                await getToken(true);
                return handleSubmit(true);
            }

            const json = error.response?.data || {};
            const title = determineErrorTitle(json);
            setErrorTitle(title);

            const mainDetail = json.details || "Submission failed.";
            const errorMessages = generateFieldErrorMessages(json.errors || [], updatedFields, fieldsJson);
            const fullMessage = `${mainDetail}${errorMessages ? "\n" + errorMessages : ""}`;

            setErrorMessage(fullMessage);
            setShowErrorToast(true);
        } finally {
            setLoading(false);
        }
    };

    const SavePDF = async () => {
        setLoadingText('Generating Loan App...')
        setLoading(true);
        await Promise.resolve();

        const updatedFields = fieldsJson
            .map(f => {
                const rawId = f.id;
                const escapedId = rawId.replace(/\./g, '\\.');
                const fieldId = `field-${rawId}`;
                const fieldMeta = fieldsJson.find(field => field.id === rawId);

                const yesCheckbox = document.getElementById(`field-${rawId}-yes`);
                const noCheckbox = document.getElementById(`field-${rawId}-no`);

                let value;

                if (yesCheckbox && noCheckbox) {
                    // Yes/No checkbox pair logic
                    if (fieldMeta?.datatype === "YN") {
                        value = yesCheckbox.checked ? "true" : noCheckbox.checked ? "false" : "";
                    } else {
                        value = yesCheckbox.checked ? "Y" : noCheckbox.checked ? "N" : "";
                    }
                } else {
                    // Single checkbox or other inputs
                    const input = document.getElementById(fieldId);
                    if (input?.type === 'checkbox') {
                        if (fieldMeta?.datatype === "YN") {
                            value = input.checked ? "true" : "false";
                        }
                        else if (fieldMeta?.datatype === "Y") {
                            value = input.checked ? "true" : "";
                        }
                        else {
                            // Check if it's a checkbox group (multiple with same ID)
                            const checkboxes = document.querySelectorAll(`input[type="checkbox"]#field-${escapedId}`);
                            if (checkboxes.length > 1) {
                                const checked = [...checkboxes].find(cb => cb.checked);
                                return {
                                    id: rawId,
                                    value: checked?.value || "",
                                    PDFFieldName: f.PDFFieldName,
                                    PDFFieldType: f.PDFFieldType,
                                    PDFGroupField: f.PDFGroupField,
                                    PDFGroupOrder: f.PDFGroupOrder,
                                    Separator: f.Separator
                                };
                            }

                            // Handle regular input (text, single checkbox, etc.)
                            const input = document.getElementById(`field-${rawId}`);
                            value =
                                input?.type === 'checkbox'
                                    ? input.checked
                                        ? input.value || "true"
                                        : ""
                                    : input?.value || "";
                        }
                    } else {
                        if (fieldMeta?.datatype === "string" && input?.tagName === "SELECT") {
                            value = input.selectedOptions?.[0]?.text || "";
                        } else {
                            value = (input?.value ?? fieldValues?.[rawId] ?? "");
                            if (fieldMeta?.datatype === "money" && typeof value === "string") {
                                value = value.replace(/,/g, "");
                            }
                        }
                    }
                }

                if (
                    typeof value === "string" &&
                    ["Select", "Select State", "Select an Option"].includes(value.trim())
                ) {
                    value = "";
                }

                return {
                    id: rawId,
                    value,
                    PDFFieldName: f.PDFFieldName,
                    PDFFieldType: f.PDFFieldType,
                    PDFGroupField: f.PDFGroupField,
                    PDFGroupOrder: f.PDFGroupOrder,
                    Separator: f.Separator
                };
            });

        const filteredFields = updatedFields.filter(f => f.PDFFieldName);

        try {
            const pdfBlob = await generateFilledPDF(filteredFields, documentationType);

            const documentTitle = "Commercial Loan Application";

            // Get selected borrower pair display name
            const borrowerSelect = document.getElementById("borrower-pair");
            const borrowerName =
                borrowerSelect?.selectedOptions?.[0]?.text?.trim() || "Borrower";

            // Clean file-unsafe characters
            const safeBorrowerName = borrowerName.replace(/[\\/:*?"<>|]/g, "");

            const fileName = documentationType === "DSCR"
                ? `CommercialApplication – DSCR – ${safeBorrowerName}.pdf`
                : `CommercialApplication – Income – ${safeBorrowerName}.pdf`;

            await getToken(true);

            // Step 1: Retrieve Documents
            let documentId = await getExistingDocumentId(token, loanid, documentTitle);

            // Step 2: Create a Document
            if (!documentId) {
                documentId = await createDocument(token, loanid, documentTitle);
            }

            // Step 3: Get URL to Upload Attachment to eFolder
            const uploadInfo = await getAttachmentUploadUrl(token, loanid, fileName, pdfBlob.size, "");

            // Step 4: Upload Attachment
            await uploadAttachmentBlob(uploadInfo.uploadUrl, uploadInfo.authorizationHeader, pdfBlob, "");

            // Step 5: Assign Attachments
            await assignAttachmentToDocument(token, loanid, documentId, uploadInfo.attachmentId);

            setShowToastText('Loan App generated successfully...');
            setShowToast(true);
            setTimeout(() => setShowToast(false), 10000);
        } catch (error) {
            const errorCode = logError("COMMERCIAL_APP_PDF_GENERATION_ERROR");
            logAppInsightError("PDF Generation Failed", {
                component: "Index",
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - Failed to generate and upload PDF to Encompass.`
            });

            setErrorMessage('PDF generation failed');
            setShowErrorToast(true);
        } finally {
            setLoading(false);
        }

    };

    const generateFieldErrorMessages = (errors, payload, fieldsJson) => {
        return errors.map((error) => {
            const match = error.summary.match(/changes\[(\d+)\]\.value/);
            if (match) {
                const index = parseInt(match[1], 10);
                const payloadField = payload[index];
                const fieldId = payloadField?.id;
                const fieldMeta = fieldsJson.find((field) => field.id === fieldId);
                const fieldName = fieldMeta?.name || `Field ID ${fieldId}`;
                return `${fieldName} (${fieldId}): ${error.details}`;
            }
            return error.details;
        }).join('\n');
    };

    const determineErrorTitle = (json) => {
        if (json.summary === "Conflict" && json.details?.includes("locked by another user")) {
            return "Loan Locked by Another User";
        }

        if (json.summary === "Bad Request" && Array.isArray(json.errors) && json.errors.length > 0) {
            return "Invalid Fields Detected";
        }

        return "Submission Error";
    };

    useEffect(() => {
        if (showToast) {
            setToastVisible(true);
            setToastAnimation("animate-fade-in-right");

            const hideTimer = setTimeout(() => {
                setToastAnimation("animate-fade-out-right");

                const vanishTimer = setTimeout(() => {
                    hideToast();
                }, 500);

                return () => clearTimeout(vanishTimer);
            }, 4000);

            return () => clearTimeout(hideTimer);
        }
    }, [showToast]);

    const handleFieldChange = (fieldId, value) => {
        setFieldValues(prev => {
            const next = { ...prev, [fieldId]: value };

            const brokerProduct = next["CX.LOAN.PROD.BROKER.SELECT"];
            const docTypeVal = next["CX.DOC.TYPE"];

            // Recompute mode when either broker product OR doc type changes
            if (fieldId === "CX.LOAN.PROD.BROKER.SELECT" || fieldId === "CX.DOC.TYPE") {
                const mode = isIncomeModeForProgram(brokerProduct, docTypeVal) ? "Income" : "DSCR";
                setDocumentationType(mode);
                setFieldsJson(mode === "Income" ? encompassFields : encompassFieldsDSCR);
            }

            return next;
        });
    };

    const hideToast = () => {
        setShowToast(false);
        setToastVisible(false);
    };

    const [selectedLoanOption, setSelectedLoanOption] = useState("");

    const isRefiOrCashOut = selectedLoanOption === "NoCash-Out Refinance" || selectedLoanOption === "Cash-Out Refinance";
    // Treated like a submitted loan (only CX.BROKER.YSP.PCT editable/saved) once disclosure-locked too.
    const isLoanSubmitted = !isEncompassWeb && ((initialSubmitDate && initialSubmitDate !== "//") || editLocked);

    useEffect(() => {
        const markDirty = () => {
            localStorage.setItem(DIRTY_FLAG_KEY, "true");
        };

        const handleFieldChange = (e) => {
            const container = e.target.closest('.loan-info-control-wrapper');
            if (!container) return;
            // If not inside that wrapper, still mark dirty for all inputs
            const tag = e.target.tagName;
            const type = e.target.type;

            if (["INPUT", "SELECT", "TEXTAREA"].includes(tag) || (tag === "BUTTON" && type === "checkbox")) {
                markDirty();
            }
        };

        document.addEventListener("input", handleFieldChange);
        document.addEventListener("change", handleFieldChange);

        return () => {
            document.removeEventListener("input", handleFieldChange);
            document.removeEventListener("change", handleFieldChange);
        };
    }, []);

    const channel = new BroadcastChannel('tpo_dirty_check_channel');

    useEffect(() => {
        const handleMessage = (event) => {
            if (event.data?.type === "TRIGGER_DIRTY_CHECK") {
                const isDirty = localStorage.getItem(DIRTY_FLAG_KEY) === "true";

                if (isDirty) {
                    setShowDirtyPrompt(true);
                } else {
                    channel.postMessage({ type: "DIRTY_CHECK_RESULT", approved: true });
                }
            }
        };

        channel.addEventListener("message", handleMessage);
        return () => channel.removeEventListener("message", handleMessage);
    }, []);

    const handleSidebarDirtyConfirm = () => {
        channel.postMessage({ type: "DIRTY_CHECK_RESULT", approved: true });
        setShowDirtyPrompt(false);
    };

    const handleSidebarDirtyCancel = () => {
        channel.postMessage({ type: "DIRTY_CHECK_RESULT", approved: false });
        setShowDirtyPrompt(false);
    };

    const isIncomeModeForProgram = (brokerProduct, docType) => {
        const dt = (docType || "").toString().trim().toUpperCase();

        // existing behavior: ACCELERATOR INCOME / BREEZY INCOME
        if (isLoanProgram(brokerProduct, LOAN_PROGRAM_CODES.PROGRAM_003)) return true;

        // ACCELERATOR / BREEZY + Full/Alt Doc => Income mode
        if (isLoanProgram(brokerProduct, LOAN_PROGRAM_CODES.PROGRAM_002) &&
            (dt === "FULL DOC" || dt === "ALT DOC")) return true;

        return false;
    };

    useEffect(() => {
        localStorage.removeItem(DIRTY_FLAG_KEY);
    }, []);

    if (isConsumerLoan) {
        return <RestrictedAccess />;
    }

    return (
        <>
            <LoadingOverlay loading={loading || isSaving} text={loadingText || 'Saving...'} />

            <Toast
                visible={toastVisible}
                type="success"
                message={showToastText}
                onClose={hideToast}
                animationClass={toastAnimation}
            />

            <Toast
                visible={warningToastVisible}
                type="warning"
                message={warningToastMessage}
                onClose={() => setWarningToastVisible(false)}
                animationClass="animate-fade-in-right"
            />

            <Modal
                title={errorTitle}
                message={errorMessage}
                isOpen={showErrorToast}
                onClose={() => setShowErrorToast(false)}
                type="error"
            />

            <DirtyCheckPrompt
                visible={showDirtyPrompt}
                onConfirm={async () => {
                    setShowDirtyPrompt(false);

                    try {
                        await handleSubmit(false);

                        //Only continue if save succeeded and dirty flag cleared
                        const stillDirty = localStorage.getItem(DIRTY_FLAG_KEY) === "true";

                        if (!stillDirty) {
                            setTimeout(() => handleSidebarDirtyConfirm(), 2000);
                        } else {
                            console.warn("Save failed — dirty flag still set. Navigation stopped.");
                        }
                    } catch (err) {
                        console.error("Error saving in DirtyCheckPrompt:", err);
                        // optional: show error toast or modal if you want
                    } finally {
                        setLoading(false);
                    }
                }}

                onDisregard={() => {
                    localStorage.removeItem(DIRTY_FLAG_KEY);
                    handleSidebarDirtyConfirm();
                    setShowDirtyPrompt(false);
                }}
                onCancel={() => {
                    handleSidebarDirtyCancel();
                    setShowDirtyPrompt(false);
                }}
            />

            <div className="space-y-10 p-25px">
                <div className="rounded-lg p-3 space-y-6 max-w-8xl mx-auto">
                    <h2 className="page-title">Commercial Loan Application</h2>
                    <div className="flex flex-wrap justify-between items-start gap-4">
                        <div className="flex-grow">
                            <BorrowerPairSection
                                ref={borrowerPairRef}
                                token={token}
                                loanid={loanid}
                                onSelect={(applicationId, index, legacyId) => {
                                    setApplicationId(applicationId);
                                    setBorrowerIndex(index);
                                    setSelectedBorrowerLegacyId(legacyId);
                                }}
                                showToast={setShowToast}
                                setToastText={setShowToastText}
                                showWarningToast={setWarningToastVisible}
                                setWarningMessage={setWarningToastMessage}
                                isReadOnly={isLoanSubmitted}
                                setLoading={setIsSaving}
                            />
                        </div>

                        <div className="flex gap-2 pt-0 pr-4 pb-4 pl-4">
                            <LoanActionButtons
                                isEncompassWeb={isEncompassWeb}
                                // isReadOnly={isLoanSubmitted}
                                onSave={() => handleSubmit(false)}
                                onGenerate={SavePDF}
                            />
                        </div>
                    </div>

                    <div className='loan-info-control-wrapper'>


                        {/* Show AdditionalInformation only if not correspondent type */}
                        {!correspondentChannel && (
                            <AdditionalInformation
                                additionalFields={additionalFields}
                                fieldValues={fieldValues}
                                bpcPrograms={bpcPrograms}
                                hiddenFieldIds={hiddenAdditionalFieldIds}
                                onFieldChange={handleFieldChange}
                                isLoanSubmitted={isLoanSubmitted}
                            />
                        )}

                        <FinancingRequest
                            fieldValues={fieldValues}
                            selectedLoanOption={selectedLoanOption}
                            setSelectedLoanOption={setSelectedLoanOption}
                        />

                        <PropertyInformation
                            fieldValues={fieldValues}
                            isRefiOrCashOut={isRefiOrCashOut}
                            onFieldChange={handleFieldChange}
                            correspondentChannel={correspondentChannel}
                        />

                        <AssetInformation
                            fieldValues={fieldValues}
                            vodList={vodList}
                            setVodList={setVodList} />

                        <REOInformation
                            ref={reoRef}
                            token={token}
                            loanid={loanid}
                            applicationId={applicationId}
                            onREOUpdate={(reos, volMeta) => {
                                setReoList(reos);
                                setAssignedVolsMap(volMeta?.previousAssignments || {});
                            }}
                        />
                        <ApplicantInformation
                            fieldValues={fieldValues}
                            showEmployment={documentationType === "Income"}
                            borrowerIndex={borrowerIndex}
                            showWarningToast={setWarningToastVisible}
                            setWarningMessage={setWarningToastMessage}
                        />
                        <PropertyAndMoneyDetails fieldValues={fieldValues} />
                        <AboutYourFinances fieldValues={fieldValues} />
                        <ApplicantDemographics fieldValues={fieldValues} />
                        <LoanOriginatorInformation fieldValues={fieldValues} />
                    </div>

                    <div className="flex justify-end gap-2 mt-4">
                        <LoanActionButtons
                            isEncompassWeb={isEncompassWeb}
                            // isReadOnly={isLoanSubmitted}
                            onSave={() => handleSubmit(false)}
                            onGenerate={SavePDF}
                        />
                    </div>
                </div>
            </div >
        </>
    );
}
